(() => {
  if (window.LumiNovaHomeV1) return;

  const coarsePointer = window.matchMedia('(hover: none), (pointer: coarse)');
  const pointerStarts = new WeakMap();
  const dialogTriggers = new WeakMap();
  const dialogVariants = new WeakMap();

  const getDialogVariants = (dialog) => {
    if (dialogVariants.has(dialog)) return dialogVariants.get(dialog);

    const data = dialog.querySelector('[data-lnh-variants]');
    let variants = [];
    try {
      variants = JSON.parse(data?.textContent || '[]');
    } catch (error) {
      console.error('[LumiNova Home Options]', error);
    }

    dialogVariants.set(dialog, variants);
    return variants;
  };

  const updateOptionsDialog = (dialog) => {
    const selectedOptions = Array.from(dialog.querySelectorAll('[data-lnh-option-index]')).map((select) => select.value);
    const variant = getDialogVariants(dialog).find((item) => (
      Array.isArray(item.options)
      && item.options.length === selectedOptions.length
      && item.options.every((value, index) => value === selectedOptions[index])
    ));
    const input = dialog.querySelector('[data-lnh-variant-id]');
    const button = dialog.querySelector('[data-lnh-quick-add-button]');
    const status = dialog.querySelector('[data-lnh-variant-status]');
    const oldPrice = dialog.querySelector('[data-lnh-variant-price-old]');
    const currentPrice = dialog.querySelector('[data-lnh-variant-price-current]');
    const priceLabel = dialog.querySelector('[data-lnh-variant-price-label]');
    const variantImage = dialog.querySelector('.lnh-options-dialog__image');
    if (!input || !button) return;

    const defaultLabel = button.dataset.defaultLabel || button.textContent.trim();
    const soldOutLabel = button.dataset.soldOutLabel || 'Sold out';
    const isAvailable = Boolean(variant?.available);

    input.value = variant?.id || '';
    button.disabled = !isAvailable;
    button.textContent = isAvailable ? defaultLabel : soldOutLabel;
    if (status) status.textContent = isAvailable ? '' : soldOutLabel;

    if (currentPrice) currentPrice.textContent = variant?.discountedPrice || variant?.price || '';
    if (oldPrice) {
      oldPrice.textContent = variant?.discountedPrice ? variant.price : (variant?.compareAtPrice || '');
      oldPrice.hidden = !oldPrice.textContent;
    }
    if (priceLabel) priceLabel.hidden = !variant?.discountedPrice;

    if (variantImage && variant?.imageUrl) {
      variantImage.src = variant.imageUrl;
      if (variant.imageSrcset) variantImage.srcset = variant.imageSrcset;
      else variantImage.removeAttribute('srcset');
      variantImage.alt = variant.imageAlt || '';
    }
  };

  const closeOptionsDialog = (dialog, restoreFocus = true) => {
    if (!dialog) return;
    const trigger = dialogTriggers.get(dialog);

    if (dialog.open && typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');

    if (!document.querySelector('[data-lnh-options-dialog][open]')) {
      document.body.classList.remove('lnh-options-dialog-open');
    }

    if (restoreFocus && trigger?.isConnected) trigger.focus();
  };

  const openOptionsDialog = (trigger) => {
    const dialogId = trigger.getAttribute('aria-controls');
    const dialog = dialogId ? document.getElementById(dialogId) : null;
    if (!dialog) return;

    dialogTriggers.set(dialog, trigger);
    updateOptionsDialog(dialog);
    document.body.classList.add('lnh-options-dialog-open');

    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');

    window.requestAnimationFrame(() => {
      const firstControl = dialog.querySelector('select, button:not([disabled])');
      firstControl?.focus();
    });
  };

  const resetSecondaryImages = (except = null) => {
    document.querySelectorAll('[data-lnh-secondary-link].is-secondary').forEach((link) => {
      if (link === except) return;
      link.classList.remove('is-secondary');
    });
  };

  const activateTab = (tab, moveFocus = false) => {
    const root = tab.closest('[data-lnh-tabs]');
    if (!root) return;

    const tabs = Array.from(root.querySelectorAll('[role="tab"]'));
    const panels = Array.from(root.querySelectorAll('[role="tabpanel"]'));

    tabs.forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.tabIndex = selected ? 0 : -1;
    });

    panels.forEach((panel) => {
      panel.hidden = panel.id !== tab.getAttribute('aria-controls');
    });

    if (moveFocus) tab.focus();
  };

  const normalizeTabs = (scope = document) => {
    scope.querySelectorAll('[data-lnh-tabs]').forEach((root) => {
      const selected = root.querySelector('[role="tab"][aria-selected="true"]') || root.querySelector('[role="tab"]');
      if (selected) activateTab(selected);
    });
  };

  document.addEventListener('pointerdown', (event) => {
    const link = event.target.closest('[data-lnh-secondary-link]');
    if (!link) return;
    pointerStarts.set(link, { x: event.clientX, y: event.clientY });
    link.dataset.lnhDragged = 'false';
  });

  document.addEventListener('pointerup', (event) => {
    const link = event.target.closest('[data-lnh-secondary-link]');
    if (!link) return;
    const start = pointerStarts.get(link);
    if (!start) return;
    const distance = Math.hypot(event.clientX - start.x, event.clientY - start.y);
    link.dataset.lnhDragged = distance > 10 ? 'true' : 'false';
    pointerStarts.delete(link);
  });

  document.addEventListener('pointercancel', (event) => {
    const link = event.target.closest('[data-lnh-secondary-link]');
    if (!link) return;
    pointerStarts.delete(link);
    link.dataset.lnhDragged = 'true';
  });

  document.addEventListener('click', (event) => {
    const optionsOpen = event.target.closest('[data-lnh-options-open]');
    if (optionsOpen) {
      event.preventDefault();
      openOptionsDialog(optionsOpen);
      return;
    }

    const optionsClose = event.target.closest('[data-lnh-options-close]');
    if (optionsClose) {
      event.preventDefault();
      closeOptionsDialog(optionsClose.closest('[data-lnh-options-dialog]'));
      return;
    }

    const optionsDialog = event.target.closest('[data-lnh-options-dialog]');
    if (optionsDialog && event.target === optionsDialog) {
      closeOptionsDialog(optionsDialog);
      return;
    }

    const tab = event.target.closest('[role="tab"][data-lnh-tab]');
    if (tab) {
      event.preventDefault();
      activateTab(tab);
      return;
    }

    const secondaryLink = event.target.closest('[data-lnh-secondary-link]');
    if (!secondaryLink) {
      resetSecondaryImages();
      return;
    }

    if (!coarsePointer.matches) return;

    if (secondaryLink.dataset.lnhDragged === 'true') {
      event.preventDefault();
      secondaryLink.dataset.lnhDragged = 'false';
      return;
    }

    if (!secondaryLink.classList.contains('is-secondary')) {
      event.preventDefault();
      resetSecondaryImages(secondaryLink);
      secondaryLink.classList.add('is-secondary');
    }
  });

  document.addEventListener('change', (event) => {
    const optionSelect = event.target.closest('[data-lnh-option-index]');
    if (!optionSelect) return;
    const dialog = optionSelect.closest('[data-lnh-options-dialog]');
    if (dialog) updateOptionsDialog(dialog);
  });

  document.addEventListener('cancel', (event) => {
    const dialog = event.target.closest('[data-lnh-options-dialog]');
    if (!dialog) return;
    event.preventDefault();
    closeOptionsDialog(dialog);
  }, true);

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-lnh-quick-add-form]');
    if (!form) return;

    event.preventDefault();
    event.stopPropagation();

    const button = form.querySelector('[data-lnh-quick-add-button]');
    const status = form.querySelector('[data-lnh-quick-add-status]');
    if (!button || button.disabled || form.dataset.lnhSubmitting === 'true') return;

    const defaultLabel = button.dataset.defaultLabel || button.textContent.trim();
    const addingLabel = button.dataset.addingLabel || defaultLabel;
    const addedLabel = button.dataset.addedLabel || defaultLabel;
    const errorLabel = button.dataset.errorLabel || defaultLabel;

    form.dataset.lnhSubmitting = 'true';
    button.disabled = true;
    button.textContent = addingLabel;
    if (status) status.textContent = addingLabel;

    try {
      const formData = new FormData(form);
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const sections = [];

      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        cartDrawer.getSectionsToRender().forEach((section) => sections.push(section.id));
      } else if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
        cartNotification.getSectionsToRender().forEach((section) => sections.push(section.id));
      } else {
        sections.push('cart-drawer', 'cart-icon-bubble');
      }

      formData.append('sections', [...new Set(sections)].join(','));
      formData.append('sections_url', window.location.pathname);

      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.description || data.message || errorLabel);

      const optionsDialog = form.closest('[data-lnh-options-dialog]');
      if (optionsDialog) closeOptionsDialog(optionsDialog, false);

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(data);
      } else if (cartNotification && typeof cartNotification.renderContents === 'function') {
        cartNotification.renderContents(data);
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: data }));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      }

      button.textContent = addedLabel;
      if (status) status.textContent = addedLabel;
    } catch (error) {
      console.error('[LumiNova Home Quick Add]', error);
      button.textContent = errorLabel;
      if (status) status.textContent = error instanceof Error ? error.message : errorLabel;
    } finally {
      window.setTimeout(() => {
        form.dataset.lnhSubmitting = 'false';
        button.disabled = false;
        button.textContent = defaultLabel;
        if (status) status.textContent = '';
      }, 1400);
    }
  });

  document.addEventListener('keydown', (event) => {
    const tab = event.target.closest('[role="tab"][data-lnh-tab]');
    if (!tab) return;

    const tabs = Array.from(tab.closest('[role="tablist"]').querySelectorAll('[role="tab"]'));
    const currentIndex = tabs.indexOf(tab);
    let nextIndex = null;

    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;

    if (nextIndex === null) return;
    event.preventDefault();
    activateTab(tabs[nextIndex], true);
  });

  document.addEventListener('shopify:section:load', (event) => normalizeTabs(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.querySelector('[data-lnh-secondary-link].is-secondary')) resetSecondaryImages();
    event.target.querySelectorAll('[data-lnh-options-dialog][open]').forEach((dialog) => closeOptionsDialog(dialog, false));
  });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest('[data-lnh-tabs]');
    if (!root) return;
    const tab = root.querySelector(`[data-lnh-block-id="${event.detail.blockId}"]`);
    if (tab) activateTab(tab);
  });

  normalizeTabs();

  window.LumiNovaHomeV1 = {
    activateTab,
    resetSecondaryImages,
    version: '1.1.0',
  };
})();
